// React
import * as React from "react";
import _ from 'lodash';
import { SketchPicker, ChromePicker, BlockPicker } from 'react-color';

// Material UI components
import { Table, TableBody, TableRow, TableRowColumn } from 'material-ui/Table';
import IconButton from 'material-ui/IconButton';
import SetIcon from 'material-ui/svg-icons/action/done';
import Popover from 'material-ui/Popover/Popover';

// Internal components
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { getStringFromRGBA } from "../Gauges/GaugesHelper";
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
import { getRandomSmallKey, isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { SettingsList } from '@Primitive/SettingsList';
import { WebNode } from '@API/WebNode';
import { Plegma } from '@API/Api';
import { WebThingPortType } from '@API/WebNodeTypes';
import { LocalizationMap } from '@Primitive/Localization/Localization';
import { WebThing, WebThingPort } from '@API/WebThing';
import { getRowMinHeight } from '@Primitive/Typography';
import "../../../Primitive/PrimitiveCss.less";

// todos:
// - remove `DisplayColorPicker` from WidgetData !!!
// - remove `Color` from WidgetData !!!
// =======================================================================================================
export interface ColorPaletteData {
    ColorPalette: {
        Key: string;
        Value: string;
        Mode: ColorPaletteMode;
        // runtime fields that need to be removed
        DisplayColorPicker: boolean;
        Color: string
    }[];
    FillWidget: boolean;
}

export interface ColorPaletteState {
    Data: ColorPaletteData;
    /**
     * Color picker index
     */
    ColorPickerIndex: number;
}
export const enum ColorPaletteMode {
    Sketch,
    Chrome,
    Block
}
// ------------------------------------------------------------------------------------------------------
class ColorPalette extends React.Component<WidgetsThingsStructs.PartialIWidgetThingsProps<ColorPaletteData>, ColorPaletteState> {
    public anchorEl: { [key: string]: any } = {};
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<ColorPaletteData>) {
        super(props);

        const data = _.cloneDeep(this.props.WidgetData);
        data.ColorPalette.forEach(element => {
            element.Color = (_.has(this.props.PortsStates, element.Key)) ? this.props.PortsStates[element.Key].State : "";
        });

        this.state = {
            ColorPickerIndex: -1,
            Data: data
        };
    }
    // ---------------------------------------------------------------------------------------------------
    handleClick = (index: number) => {
        if (!isDefined(index))
            index = -1;
        this.setState({ ColorPickerIndex: index });
    };
    // ---------------------------------------------------------------------------------------------------
    handleColorPickerChange = (color) => {
        this.setState((s) => {
            s.Data.ColorPalette[s.ColorPickerIndex].Color = getStringFromRGBA(color.rgb);
            return s;
        });
    };
    // ---------------------------------------------------------------------------------------------------
    componentWillReceiveProps(nextProps: WidgetsThingsStructs.PartialIWidgetThingsProps<ColorPaletteData>) {
        if (!_.isEqual(this.props.WidgetData, nextProps.WidgetData))
            this.setState({
                Data: nextProps.WidgetData,
                ColorPickerIndex: -1
            });
    }
    // ---------------------------------------------------------------------------------------------------
    chooseColorPaletteMode(c): JSX.Element {
        switch (c.Mode) {
            case ColorPaletteMode.Sketch:
                return <SketchPicker color={c.Color} onChange={this.handleColorPickerChange} />;
            case ColorPaletteMode.Chrome:
                return <ChromePicker color={c.Color} onChange={this.handleColorPickerChange} />;
            case ColorPaletteMode.Block:
                return <BlockPicker color={c.Color} onChange={this.handleColorPickerChange} />;
            default: break;
        }
    };
    // ---------------------------------------------------------------------------------------------------
    renderColorPicker(c, index, portIsActive: boolean): React.ReactNode {
        return (
            <div
                style={{
                    padding: '5px',
                    background: '#fff',
                    borderRadius: '1px',
                    boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
                    display: 'inline-block',
                    opacity: (portIsActive) ? 1 : 0.2,
                    cursor: (portIsActive) ? "pointer" : "not-allowed"
                }}
                onClick={(portIsActive) ? () => this.handleClick(index) : null}>
                <div
                    ref={(r) => this.anchorEl[index] = r}
                    style={{
                        width: this.props.width < 200 ? '14px' : '36px',
                        height: this.props.width < 200 ? '10px' : '14px',
                        borderRadius: '2px',
                        background: c.Color,
                        opacity: (portIsActive) ? 1 : 0.2,
                        cursor: (portIsActive) ? "pointer" : "not-allowed"
                    }}
                />
            </div>
        );
    }
    // ---------------------------------------------------------------------------------------------------
    render() {

        let minHeight = undefined;
        if (this.props.WidgetData.FillWidget)
            minHeight = getRowMinHeight(
                this.props.WidgetData.ColorPalette.length,
                this.props.height,
                48,
                0
            );
        const { ColorPickerIndex } = this.state;

        return (
            <div style={{ padding: WidgetsThingsStructs.defaultWidgetContainerPadding }}>

                {ColorPickerIndex != -1 ?
                    <Popover
                        open={true}
                        anchorEl={this.anchorEl[this.state.ColorPickerIndex]}
                        anchorOrigin={{ "horizontal": "left", "vertical": "bottom" }}
                        targetOrigin={{ "horizontal": "left", "vertical": "top" }}
                        onRequestClose={() => this.handleClick(-1)}
                    >
                        {this.chooseColorPaletteMode(this.state.Data.ColorPalette[ColorPickerIndex])}
                    </Popover>
                    : null
                }

                {
                    <Table height="100%" selectable={false} multiSelectable={false}>
                        <TableBody
                            displayRowCheckbox={false}
                            stripedRows={true}
                        >
                            {
                                this.state.Data.ColorPalette.map((c, index) => {
                                    const portIsActive = (_.has(this.props.PortsStates, c.Key)) ? this.props.PortsStates[c.Key].IsActive : false;

                                    let value = this.props.LocalizationMap['value_' + c.Key];
                                    if (IsNullOrEmpty(value))
                                        value = c.Value;

                                    return (
                                        <TableRow key={"Color-Row-" + c.Key} >
                                            <TableRowColumn key={"Row-Column-Label" + c.Key} style={{ textAlign: "center", fontSize: this.props.width < 200 ? 10 : 13, paddingLeft: this.props.width < 200 ? 10 : 24, cursor: (portIsActive) ? "auto" : "not-allowed", height: minHeight }}>
                                                {value}
                                            </TableRowColumn>
                                            <TableRowColumn key={"Row-Column-Color-" + c.Key} style={{ textAlign: "center", height: minHeight, paddingLeft: this.props.width < 200 ? 10 : 24 }}>
                                                {this.renderColorPicker(c, index, portIsActive)}
                                            </TableRowColumn>
                                            <TableRowColumn key={"Row-Column-Set-Icon-" + c.Key} style={{ textAlign: "center" }}>
                                                <IconButton key={"Color-Palette-Set-Icon" + c.Key} style={{ paddingLeft: this.props.width < 200 ? 2 : 24, marginLeft: this.props.width < 200 ? -20 : 0, textAlign: "center", opacity: (portIsActive) ? 1 : 0.2, cursor: (portIsActive) ? "pointer" : "not-allowed" }} touch={true}
                                                    onClick={() => { if (portIsActive) this.props.SendPortEvent(c.Key, c.Color) }}>
                                                    <SetIcon />
                                                </IconButton>
                                            </TableRowColumn>
                                        </TableRow>
                                    );
                                })
                            }
                        </TableBody>
                    </Table>
                }
            </div>
        );
    }
}

interface ColorPaletteWidgetSettingsState {
    WidgetData: ColorPaletteData;
    UnSavedData: boolean;
}

class ColorPaletteWidgetSettings extends React.Component<IWidgetSettingsProps<ColorPaletteData>, ColorPaletteWidgetSettingsState> {
    constructor(props: IWidgetSettingsProps<ColorPaletteData>) {
        super(props);

        this.state = {
            WidgetData: _.cloneDeep(this.props.WidgetData),
            UnSavedData: false
        };

        // Bind functions
        this.saveData = this.saveData.bind(this);
    }
    // ---------------------------------------------------------------------------------------------------
    saveData() {
        if (isDefined(this.props.onWidgetDataUpdate))
            this.props.onWidgetDataUpdate(this.state.WidgetData);
        this.setState({ UnSavedData: false });
    }

    // ------------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<ColorPaletteData>[] = [
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.ItemsMgmtDialog,
                        label: 'Color Palette',
                        tooltip: 'Define the properties of the pickers. Sets value and mode(Sketch, Chrome, Block)',
                        dataPath: "ColorPalette",
                        dialogStaticSize: { w: 0.7, h: 0.8 },
                        itemsInfo: [
                            {
                                type: SettingsType.String,
                                label: "Color Palette",
                                dataPath: "Value"
                            },
                            {
                                type: SettingsType.DropDown,
                                label: "Color Palette Mode",
                                dataPath: "Mode",
                                items: [
                                    { value: ColorPaletteMode.Sketch, label: "Sketch" },
                                    { value: ColorPaletteMode.Chrome, label: "Chrome" },
                                    { value: ColorPaletteMode.Block, label: "Block" }
                                ]
                            }
                        ],
                        createNewItem: (data) => {
                            let thing = WebNode.GetInstanceKey(this.props.NodeKey).GetThing(this.props.ThingKey);
                            return {
                                Key: thing.GetNextGeneratedPortKey(),
                                Value: "Color palette",
                                DisplayColorPicker: false,
                                Color: "rgba(0,0,0,1)",
                                Mode: ColorPaletteMode.Sketch
                            }
                        }
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Fill Space",
                        tooltip: "Fills the widget's available space.",
                        dataPath: "FillWidget"
                    }
                ]
            },
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
    // ---------------------------------------------------------------------------------------------------
}

// =======================================================================================================
// Color Palette Description interface
export class ColorPaletteWidget extends IWidgetThingsClass<ColorPaletteData> {
    // ------------------------------------------------------------------------------------------------
    public Render(key: string, width: number, height: number): React.ReactNode {
        return <ColorPalette />;
    }
    // ------------------------------------------------------------------------------------------------
    public RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <ColorPaletteWidgetSettings
            key={"Settings-" + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    updateThing() {
        let wData = this.WidgetData;
        let webNode = WebNode.GetInstanceKey(this.NodeKey);
        let thing = webNode.GetThing(this.ThingKey);

        // Clean old ports
        thing.Ports = {};

        // Add the ports
        _.forEach(wData.ColorPalette, (c, index) => {
            thing.AddPort(new WebThingPort(
                {
                    Name: c.Value,
                    PortKey: c.Key,
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Output,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: "",
                    State: c.Color
                }, WebThingPortType.Data), false);
        });

        webNode.GetModule(this.WebModuleKey).UpdateThing(thing);
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        let wData = this.WidgetData;
        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = [];
        _.forEach(wData.ColorPalette, (btn, index) => {
            ports.push(new WebThingPort(
                {
                    Name: btn.Value,
                    PortKey: btn.Key,
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Output,
                    ConfFlags: Plegma.ePortConf.None,
                    Description: "",
                    State: "rgba(0,0,0,1)"
                }, WebThingPortType.Data
            ));
        });

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): ColorPaletteData {
        return {
            ColorPalette: [],
            FillWidget: false
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return ColorPaletteWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data: ColorPaletteData = {
            ColorPalette: [
                { Key: "1_" + getRandomSmallKey(), Value: "Color palette 1", DisplayColorPicker: false, Color: "rgba(0,0,0,1)", Mode: ColorPaletteMode.Sketch },
                { Key: "2_" + getRandomSmallKey(), Value: "Color palette 2", DisplayColorPicker: false, Color: "rgba(0,0,0,1)", Mode: ColorPaletteMode.Sketch },
                { Key: "3_" + getRandomSmallKey(), Value: "Color palette 3", DisplayColorPicker: false, Color: "rgba(0,0,0,1)", Mode: ColorPaletteMode.Sketch }],
            FillWidget: false
        };

        return {
            Title: "Color Palette",
            WidgetTitle: "",
            Key: "ColorPaletteWidget",
            Type: "ColorPaletteWidget",
            Data: data,
            Description: "Color Palette widget",
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + "/png/colormap.png"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary(): LocalizationMap {
        const res = {};

        // expose title (if any)
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        _.forEach(this.WidgetData.ColorPalette, (palette) => {
            if (!IsNullOrEmpty(palette.Value))
                res['value_' + palette.Key] = palette.Value;
        });

        return res;
    };
    // ------------------------------------------------------------------------------------------------
}
// =======================================================================================================